import { ConsoleID, getConsoleState } from 'app/config/Logger';
import React, { MouseEvent, PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './Popup.module.scss';

export type TipDirection = 'Up' | 'Down' | 'Left' | 'Right';

type PopupOwnProps = {
  className?: string;
  containerClassName?: string;
  visible?: boolean;
  disabled?: boolean;
  popupContent?: React.ReactNode;
  /** @default {TipDirection} `Down` */
  tipDirection?: TipDirection;
};

const CONSOLE_ON = getConsoleState(ConsoleID.Popup);

type PopupProps = PropsWithChildren<PopupOwnProps>;

const Popup: React.FC<PopupProps> = ({
  className = '',
  containerClassName = '',
  children,
  visible = false,
  disabled = false,
  popupContent,
  tipDirection = 'Down',
}) => {
  const popupTargetRef = useRef<HTMLDivElement>(null);
  const popupContentRef = useRef<HTMLDivElement>(null);

  const baseCls = `${styles.PopupTarget} ${className}`;

  const [showPopup, setShowPopup] = useState(false);
  const [visibilityCls, setVisibilityCls] = useState<string>('');

  useEffect(() => {
    setVisibilityCls(showPopup ? styles.Show : `${styles.Show} ${styles.Hide}`);
  }, [showPopup]);

  useEffect(() => {
    setShowPopup(visible);
  }, [visible]);

  // Position popup relative to trigger element
  useLayoutEffect(() => {
    if (!showPopup || !popupTargetRef.current || !popupContentRef.current) {
      return;
    }

    const viewport = document.documentElement;
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;

    const trigger = popupTargetRef.current;
    const popup = popupContentRef.current;

    const popupWidth = popup.offsetWidth;
    const popupHeight = popup.offsetHeight;
    const triggerWidth = trigger.offsetWidth;
    const triggerHeight = trigger.offsetHeight;

    // Get trigger position in viewport
    const triggerRect = trigger.getBoundingClientRect();

    let top = 0;
    let left = 0;

    // Calculate initial position based on tip direction (relative to trigger)
    switch (tipDirection) {
      case 'Down':
        top = -popupHeight - 10;
        left = triggerWidth / 2 - popupWidth / 2;
        break;
      case 'Up':
        top = triggerHeight + 10;
        left = triggerWidth / 2 - popupWidth / 2;
        break;
      case 'Left':
        top = triggerHeight / 2 - popupHeight / 2;
        left = triggerWidth + 10;
        break;
      case 'Right':
        top = triggerHeight / 2 - popupHeight / 2;
        left = -popupWidth - 10;
        break;
    }

    // Check viewport boundaries and adjust
    // Calculate absolute position in viewport
    const absoluteTop = triggerRect.top + top;
    const absoluteLeft = triggerRect.left + left;
    const absoluteRight = absoluteLeft + popupWidth;
    const absoluteBottom = absoluteTop + popupHeight;

    // Store original calculated positions
    const originalLeft = left;
    const originalTop = top;

    // Adjust if hitting boundaries
    if (absoluteLeft < 10) {
      left += 10 - absoluteLeft;
    } else if (absoluteRight > viewportWidth - 10) {
      left -= absoluteRight - (viewportWidth - 10);
    }

    if (absoluteTop < 10) {
      top += 10 - absoluteTop;
    } else if (absoluteBottom > viewportHeight - 10) {
      top -= absoluteBottom - (viewportHeight - 10);
    }

    // Calculate delta and adjust tip position
    const deltaLeft = left - originalLeft;
    const deltaTop = top - originalTop;

    // Tip moves opposite to popup shift by half the delta
    const tipOffsetX = -deltaLeft;
    const tipOffsetY = -deltaTop;

    popup.style.top = `${top}px`;
    popup.style.left = `${left}px`;
    popup.style.setProperty('--tip-offset-x', `calc(50% + ${tipOffsetX}px)`);
    popup.style.setProperty('--tip-offset-y', `calc(50% + ${tipOffsetY}px)`);
  }, [showPopup, tipDirection]);

  const mouseEnterEventHandler = useCallback(
    (e: MouseEvent) => {
      // Check if we're hovering over the popup content itself when it's hidden
      const target = e.target as HTMLElement;
      const currentPopupContainer = popupContentRef.current;

      // If the target is within our own popup container and popup is not shown, ignore
      if (currentPopupContainer && currentPopupContainer.contains(target) && !showPopup) {
        if (CONSOLE_ON) console.log('Ignoring mouse enter on hidden popup content');
        return;
      }

      // Check if target is within any hidden popup container (for nested popups)
      const anyPopupContainer = target.closest('[class*="PopupContainer"]');
      if (anyPopupContainer && anyPopupContainer !== currentPopupContainer) {
        const computedStyle = window.getComputedStyle(anyPopupContainer);
        const hasPointerEvents = computedStyle.pointerEvents !== 'none';

        if (!hasPointerEvents) {
          if (CONSOLE_ON) console.log('Ignoring mouse enter on element with disabled pointer events');
          return;
        }
      }

      if (CONSOLE_ON) console.log('Mouse enter - showing popup', e.currentTarget.getBoundingClientRect());
      setShowPopup(true);
    },
    [showPopup]
  );

  const mouseLeaveEventHandler = useCallback((e: MouseEvent) => {
    if (CONSOLE_ON) console.log(e.currentTarget.getBoundingClientRect());
    setShowPopup(false);
  }, []);

  const clickEventHandler = useCallback(() => {
    setShowPopup((prev) => !prev);
  }, []);

  if (disabled) return <>{children}</>;

  return (
    <>
      <div
        ref={popupTargetRef}
        onMouseEnter={mouseEnterEventHandler}
        onMouseLeave={mouseLeaveEventHandler}
        onClick={clickEventHandler}
        className={baseCls}
      >
        {children}
        <div
          ref={popupContentRef}
          className={`${styles.PopupContainer} ${visibilityCls} ${styles[tipDirection]} ${containerClassName}`.trim()}
        >
          {popupContent}
        </div>
      </div>
    </>
  );
};

export default Popup;
